// Geo.tsx
import React, { useRef, useMemo } from 'react';
import { Config, FromFluxResult, Plot } from '@influxdata/giraffe';

interface TableChartProps {
  style?: React.CSSProperties;
  data: FromFluxResult;
  metrics: string[];
  preview: boolean;
}
interface ExtendedTileServerConfig {
  tileServerUrl: string;
  attribution?: string;
  noWrap?: boolean;
  preview?: boolean;
}

export const GeoChart = ({ style, data, metrics, preview }: TableChartProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [zoom] = React.useState(8);
  const safeZoom = Math.max(2, zoom);
  const geoLayers: any[] = [];

  if (!preview) {
    geoLayers.push({
      type: 'pointMap',
      colorDimension: { label: 'Device Status' },
      colorField: 'deviceState',
      colors: [
        { value: 'OFFLINE', hex: '#DC143C' },
        { value: 'ONLINE', hex: '#00C957' },
      ],
      isClustered: true,
      clusterAggregationFunction: 'count',
      tooltipColumns: ['_time', 'city', 'state', ...metrics],
    });
  }

  const config: Config = useMemo(
    () => ({
      table: data.table,
      showAxes: false,
      layers: [
        {
          type: 'geo',
          lat: 37.0902,
          lon: -95.7129,
          zoom: safeZoom,
          detectCoordinateFields: false,
          allowPanAndZoom: !preview,
          useS2CellID: false,
          latLonColumns: {
            lat: { key: 'latitude', column: 'latitude' },
            lon: { key: 'longitude', column: 'longitude' },
          },
          layers: geoLayers,
          tileServerConfiguration: {
            tileServerUrl: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
            noWrap: true,
          } as ExtendedTileServerConfig,
        },
      ],
    }),
    [data, metrics, preview, safeZoom, geoLayers],
  );

  // generate key so Plot fully resets when data changes
  const rowCount = data?.table?.length;
  const timeCol = data?.table?.getColumn('_time', 'number'); 
  const lastTime = rowCount > 0 ? timeCol?.[rowCount - 1] : null;
  const keyValue = rowCount > 0 ? `${rowCount}-${lastTime}` : 'no-data';

  return (
    <div
      ref={containerRef}
      style={{
        width: '100%',
        height: '100%',
        padding: '10px',
        display: 'flex',
        position: 'relative',
        overflow: 'hidden',
        ...style,
      }}
    >
      <style>
        {`
          .leaflet-tile-pane {
            background-color: transparent !important;
          }
          .leaflet-container {
            background: none !important;
          }
        `}
      </style>
      <Plot key={keyValue} config={config} />
    </div>
  );
};
